import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, ArrowLeft, Plus, Search, Filter, MoreHorizontal, Eye, Printer,
  Send, CheckCircle, XCircle, Clock, AlertCircle, Loader2, Trash2, Download,
  Calendar, DollarSign, Users, RefreshCw, Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import { format } from "date-fns";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300", icon: FileText },
  sent: { label: "Sent", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", icon: Send },
  paid: { label: "Paid", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400", icon: CheckCircle },
  overdue: { label: "Overdue", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", icon: AlertCircle },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500", icon: XCircle }
};

export default function InvoicesPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state for new invoice
  const [formData, setFormData] = useState({
    client_id: "",
    items: [{ description: "", quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0 }],
    currency: "USD",
    due_date: "",
    po_number: "",
    template_id: "",
    notes: "",
    terms: ""
  });
  const [templates, setTemplates] = useState([]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [invoicesRes, clientsRes, statsRes, templatesRes] = await Promise.all([
        api.get("/invoices"),
        api.get("/clients"),
        api.get("/invoices/summary/stats"),
        api.get("/invoice-templates")
      ]);
      setInvoices(invoicesRes.data || []);
      setClients(clientsRes.data || []);
      setStats(statsRes.data);
      setTemplates(templatesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t("failed_load_data", "dashboard") || "Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return format(new Date(date), "MMM dd, yyyy");
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = 
      inv.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.client_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: "", quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0 }]
    }));
  };

  const removeLineItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateItemTotal = (item) => {
    const subtotal = item.quantity * item.unit_price;
    const discount = subtotal * (item.discount_percent / 100);
    const taxable = subtotal - discount;
    const tax = taxable * (item.tax_percent / 100);
    return taxable + tax;
  };

  const calculateInvoiceTotal = () => {
    return formData.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  const handleCreateInvoice = async () => {
    if (!formData.client_id) {
      toast.error("Please select a client");
      return;
    }
    if (formData.items.length === 0 || !formData.items[0].description) {
      toast.error("Please add at least one line item");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null
      };
      await api.post("/invoices", payload);
      toast.success(t("invoice_created", "company") || "Invoice created successfully");
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      await api.patch(`/invoices/${invoiceId}/status?status=${newStatus}`);
      toast.success(`Invoice marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update invoice status");
    }
  };

  const handlePreviewInvoice = async (invoice) => {
    try {
      const response = await api.get(`/invoices/${invoice.id}/html`);
      setPreviewHtml(response.data);
      setSelectedInvoice(invoice);
      setShowPreviewDialog(true);
    } catch (error) {
      console.error("Error fetching invoice preview:", error);
      toast.error("Failed to load invoice preview");
    }
  };

  const handlePrintInvoice = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(previewHtml);
    printWindow.document.close();
    printWindow.print();
  };

  const handleSendEmail = async (invoice) => {
    try {
      const response = await api.post(`/invoices/${invoice.id}/send-email`);
      toast.success(response.data.message || "Invoice sent successfully");
      fetchData();
    } catch (error) {
      console.error("Error sending email:", error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes("SMTP")) {
        toast.error("Please configure your email settings in Settings first");
      } else {
        toast.error(error.response?.data?.detail || "Failed to send invoice");
      }
    }
  };

  const handleDownloadPdf = async (invoice) => {
    try {
      const response = await api.get(`/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      if (error.response?.status === 501) {
        toast.error("PDF generation not available. Use Print to save as PDF.");
      } else {
        toast.error("Failed to download PDF");
      }
    }
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice) return;
    
    try {
      await api.delete(`/invoices/${selectedInvoice.id}`);
      toast.success("Invoice deleted");
      setShowDeleteDialog(false);
      setSelectedInvoice(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Failed to delete invoice");
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: "",
      items: [{ description: "", quantity: 1, unit_price: 0, discount_percent: 0, tax_percent: 0 }],
      currency: "USD",
      due_date: "",
      po_number: "",
      template_id: "",
      notes: "",
      terms: ""
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card">
          <div className="max-w-[1400px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </header>
        <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-[400px]" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="invoices-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-lg flex items-center gap-2">
                <FileText className="h-5 w-5 text-[hsl(var(--ff-primary))]" />
                {t("invoices", "company") || "Invoices"}
              </h1>
              <p className="text-xs text-muted-foreground">
                {t("manage_invoices", "company") || "Create and manage invoices"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={fetchData} data-testid="refresh-button">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-invoice-button">
              <Plus className="h-4 w-4 mr-2" />
              {t("new_invoice", "company") || "New Invoice"}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card data-testid="stat-total">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <FileText className="h-4 w-4" />
                <span className="text-sm">{t("total_invoiced", "company") || "Total Invoiced"}</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(stats?.total_invoiced || 0)}</p>
              <p className="text-xs text-muted-foreground">{stats?.total_invoices || 0} invoices</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-paid">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-600 mb-1">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">{t("paid", "common") || "Paid"}</span>
              </div>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(stats?.total_paid || 0)}</p>
              <p className="text-xs text-muted-foreground">{stats?.by_status?.paid?.count || 0} invoices</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-outstanding">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-amber-600 mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-sm">{t("outstanding", "company") || "Outstanding"}</span>
              </div>
              <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats?.total_outstanding || 0)}</p>
              <p className="text-xs text-muted-foreground">{(stats?.by_status?.sent?.count || 0) + (stats?.by_status?.overdue?.count || 0)} invoices</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-overdue">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-600 mb-1">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">{t("overdue", "common") || "Overdue"}</span>
              </div>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(stats?.by_status?.overdue?.amount || 0)}</p>
              <p className="text-xs text-muted-foreground">{stats?.by_status?.overdue?.count || 0} invoices</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("search_invoices", "company") || "Search by invoice # or client..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="status-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all_status", "company") || "All Status"}</SelectItem>
              <SelectItem value="draft">{t("draft", "company") || "Draft"}</SelectItem>
              <SelectItem value="sent">{t("sent", "company") || "Sent"}</SelectItem>
              <SelectItem value="paid">{t("paid", "common") || "Paid"}</SelectItem>
              <SelectItem value="overdue">{t("overdue", "common") || "Overdue"}</SelectItem>
              <SelectItem value="cancelled">{t("cancelled", "company") || "Cancelled"}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Invoices Table */}
        <Card>
          <CardContent className="p-0">
            {filteredInvoices.length === 0 ? (
              <div className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">{t("no_invoices", "company") || "No invoices found"}</p>
                <Button variant="outline" className="mt-4" onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create your first invoice
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("invoice_number", "company") || "Invoice #"}</TableHead>
                    <TableHead>{t("client", "company") || "Client"}</TableHead>
                    <TableHead>{t("issue_date", "company") || "Issue Date"}</TableHead>
                    <TableHead>{t("due_date", "company") || "Due Date"}</TableHead>
                    <TableHead className="text-right">{t("total", "common") || "Total"}</TableHead>
                    <TableHead className="text-right">{t("paid", "common") || "Paid"}</TableHead>
                    <TableHead className="text-right">{t("balance", "common") || "Balance"}</TableHead>
                    <TableHead>{t("status", "common") || "Status"}</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => {
                    const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;
                    const StatusIcon = statusConfig.icon;
                    const paidAmount = invoice.paid_amount || 0;
                    const balanceDue = (invoice.total_amount || 0) - paidAmount;
                    return (
                      <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                        <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                        <TableCell>{invoice.client_name || "-"}</TableCell>
                        <TableCell>{formatDate(invoice.issue_date)}</TableCell>
                        <TableCell>{formatDate(invoice.due_date)}</TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(invoice.total_amount, invoice.currency)}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {paidAmount > 0 ? formatCurrency(paidAmount, invoice.currency) : "-"}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${balanceDue > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                          {formatCurrency(balanceDue, invoice.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusConfig.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`invoice-menu-${invoice.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handlePreviewInvoice(invoice)}>
                                <Eye className="h-4 w-4 mr-2" /> {t("view_invoice", "company") || "View Invoice"}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handlePreviewInvoice(invoice)}>
                                <Printer className="h-4 w-4 mr-2" /> {t("print", "company") || "Print"}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownloadPdf(invoice)}>
                                <Download className="h-4 w-4 mr-2" /> {t("download_pdf", "company") || "Download PDF"}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleSendEmail(invoice)}>
                                <Mail className="h-4 w-4 mr-2" /> {t("send_via_email", "company") || "Send via Email"}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {invoice.status === "draft" && (
                                <DropdownMenuItem onClick={() => handleUpdateStatus(invoice.id, "sent")}>
                                  <Send className="h-4 w-4 mr-2" /> {t("mark_as_sent", "company") || "Mark as Sent"}
                                </DropdownMenuItem>
                              )}
                              {(invoice.status === "sent" || invoice.status === "overdue") && (
                                <DropdownMenuItem onClick={() => handleUpdateStatus(invoice.id, "paid")}>
                                  <CheckCircle className="h-4 w-4 mr-2" /> {t("mark_as_paid", "company") || "Mark as Paid"}
                                </DropdownMenuItem>
                              )}
                              {invoice.status !== "cancelled" && invoice.status !== "paid" && (
                                <DropdownMenuItem onClick={() => handleUpdateStatus(invoice.id, "cancelled")}>
                                  <XCircle className="h-4 w-4 mr-2" /> {t("cancel_invoice", "company") || "Cancel Invoice"}
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setShowDeleteDialog(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Invoice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("create_invoice", "company") || "Create Invoice"}</DialogTitle>
            <DialogDescription>Fill in the invoice details below</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Client Selection */}
            <div className="space-y-2">
              <Label>{t("client", "company") || "Client"} *</Label>
              <Select value={formData.client_id} onValueChange={(value) => setFormData(prev => ({ ...prev, client_id: value }))}>
                <SelectTrigger data-testid="client-select">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Line Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t("line_items", "company") || "Line Items"}</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </div>
              
              {formData.items.map((item, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <Label className="text-xs">Description</Label>
                      <Input
                        placeholder="Item description"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, "description", e.target.value)}
                        data-testid={`item-desc-${index}`}
                      />
                    </div>
                    {formData.items.length > 1 && (
                      <Button variant="ghost" size="icon" className="mt-5" onClick={() => removeLineItem(index)}>
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <Label className="text-xs">Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, "quantity", parseFloat(e.target.value) || 1)}
                        data-testid={`item-qty-${index}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Unit Price</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                        data-testid={`item-price-${index}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Discount %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={item.discount_percent}
                        onChange={(e) => updateLineItem(index, "discount_percent", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Tax %</Label>
                      <Input
                        type="number"
                        min="0"
                        value={item.tax_percent}
                        onChange={(e) => updateLineItem(index, "tax_percent", parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Total</Label>
                      <div className="h-10 px-3 flex items-center bg-muted rounded-md font-medium">
                        {formatCurrency(calculateItemTotal(item), formData.currency)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Invoice Total */}
              <div className="flex justify-end">
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Invoice Total</p>
                  <p className="text-2xl font-bold">{formatCurrency(calculateInvoiceTotal(), formData.currency)}</p>
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("currency", "common") || "Currency"}</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD - US Dollar</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="GBP">GBP - British Pound</SelectItem>
                    <SelectItem value="ARS">ARS - Argentine Peso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("due_date", "company") || "Due Date"}</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  data-testid="due-date-input"
                />
              </div>
            </div>

            {/* PO Number and Template */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("po_number", "invoice_templates") || "P.O. Number"}</Label>
                <Input
                  value={formData.po_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, po_number: e.target.value }))}
                  placeholder={t("po_number_placeholder", "invoice_templates") || "e.g., PO-12345"}
                  data-testid="po-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("invoice_template", "invoice_templates") || "Invoice Template"}</Label>
                <Select 
                  value={formData.template_id || "default"} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, template_id: value === "default" ? "" : value }))}
                >
                  <SelectTrigger data-testid="template-select">
                    <SelectValue placeholder={t("use_default_template", "invoice_templates") || "Use Default Template"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">{t("use_default_template", "invoice_templates") || "Use Default Template"}</SelectItem>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name} {template.is_default && "⭐"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("notes", "common") || "Notes"}</Label>
              <Textarea
                placeholder="Additional notes for the client..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                data-testid="notes-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t("terms", "company") || "Terms & Conditions"}</Label>
              <Textarea
                placeholder="Payment terms, late fees, etc..."
                value={formData.terms}
                onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                data-testid="terms-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              {t("cancel", "common") || "Cancel"}
            </Button>
            <Button onClick={handleCreateInvoice} disabled={isSubmitting} data-testid="submit-invoice-button">
              {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("create_invoice", "company") || "Create Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoice Preview - {selectedInvoice?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto border rounded-lg bg-white">
            <iframe
              srcDoc={previewHtml}
              className="w-full h-[600px]"
              title="Invoice Preview"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
            <Button onClick={handlePrintInvoice}>
              <Printer className="h-4 w-4 mr-2" />
              Print Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice {selectedInvoice?.invoice_number}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInvoice} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
